import { useNavigate } from 'react-router-dom'

import { Box, CheckBox, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { useHover } from 'modules/core/lib/hoverContext'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type { Column } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import {
  useSelectionDispatch,
  useSelectionState,
} from 'modules/shift_management/hooks/useSelection'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import View, { GROUP_TYPES, VIEW_TYPES } from 'modules/shift_management/store/internal/models/View'

import SortEmployeesOptions from './SortEmployeesOptions'

export interface HeaderContentProps {
  heading: Column
  index: number
  isChecked: boolean
  onClick: () => void
  highlighted?: boolean
  canManageShifts: boolean
  contractVersions: ContractVersions
}

const HeaderContent = ({
  heading,
  index,
  isChecked,
  onClick,
  highlighted,
  canManageShifts,
  contractVersions,
}: HeaderContentProps) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const { navigation } = useNavigation()

  const hover = useHover()
  const navigate = useNavigate()

  const [{ view }, dispatchSchedule] = useSchedule()
  const { isEnabled } = useSelectionState()
  const dispatchSelection = useSelectionDispatch()
  const [, dispatchPagination] = usePagination()
  const [, dispatchFilters] = useFilters()
  const [, dispatchShiftsRows] = useShiftsRows()
  const [, dispatchUI] = useUI()

  const { date } = heading
  const isWeeklyByEmployees = view.isWeekly && view.isByEmployees
  const isDailyViewEnabled = hasFeatureFlag(ShiftManagementFeatureFlags.SHIFT_MANAGEMENT_DAILY_VIEW)
  // First cell should not be redirectable
  const canRedirectToDaily = isWeeklyByEmployees && isDailyViewEnabled && index != 0

  const isCheckboxEnabled = canManageShifts && !view.isDaily && (hover || isEnabled)

  const redirectToSelectedDay = () => {
    const view = new View({
      date: date,
      viewType: VIEW_TYPES.daily,
      groupType: GROUP_TYPES.employees,
    })

    track(trackingEvents.shiftManagement.selectShiftManagementView, {
      view: view.viewType,
      view_type_by: view.groupType,
      from: 'table_days_header',
    })

    navigate(
      navigation.shifts.customViewGroup.fullPath({
        viewType: view.viewType,
        groupType: view.groupType,
        year: view.startRangeYear,
        month: view.startRangeMonth,
        day: view.startRangeDay,
      })
    )
    dispatchUI({ type: 'LOADING' })
    dispatchSchedule({ type: 'RESET_VIEW', view })
    dispatchSelection({ type: 'CLEAR_SELECTION' })
    dispatchFilters({ type: 'RESET_SHIFT_IDS' })
    dispatchShiftsRows({ type: 'RESET_SHIFTS_ROWS' })
    dispatchPagination({ type: 'RESET_PAGINATION' })
  }

  const redirectToDailyView = () => {
    if (!canRedirectToDaily) return

    return redirectToSelectedDay
  }

  return (
    <Box flexDirection="row" height="full" width="full" alignItems="center" gap="s4">
      <Box flexDirection="row" justifyContent={view.isDaily ? 'center' : 'flexStart'} width="full">
        {index == 0 && view.isByEmployees ? (
          <SortEmployeesOptions
            isCheckboxEnabled={isCheckboxEnabled}
            isChecked={isChecked}
            onClick={onClick}
            contractVersions={contractVersions}
          />
        ) : (
          <Box flexDirection="row" width="full" alignItems="center">
            <Box
              flexDirection="row"
              justifyContent="flexStart"
              width="full"
              cursor={canRedirectToDaily ? 'pointer' : 'default'}
              onClick={redirectToDailyView()}
            >
              <Text size="100" weight="bold" color={highlighted ? 'grey1000' : 'grey700'}>
                {heading.headerCell.toUpperCase()}
              </Text>
            </Box>
            <Box width="full" flexDirection="row" data-testid="menu-order" justifyContent="flexEnd">
              {isCheckboxEnabled && <CheckBox checked={isChecked} onClick={onClick} />}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default observer(HeaderContent)
